import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { HotkeysService } from '@helios/hotkeys'
import {
  RootState,
  selectActiveSession,
  selectCommands,
  selectSelectedReconDate,
  setCommands,
} from '@helios/state-management'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { Store } from '@ngrx/store'
import { toISOFormat } from '@northfork/basics'
import { ToastService } from '@northfork/ng-basics'
// eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
import { GenericConfirmationModalService } from 'libs/generic-components/src/lib/generic-confirmation-modal/generic-confirmation-modal.service'
import { Subject } from 'rxjs'
import { distinctUntilChanged, map, take, takeUntil, tap } from 'rxjs/operators'
import { Command } from '../models/command.model'
import { CommandService } from '../services/command/command.service'

@Component({
  selector: 'hs-commands-drawer',
  templateUrl: './commands-drawer.component.html',
  styleUrls: ['./commands-drawer.component.scss'],
})
export class CommandsDrawerComponent implements OnInit, OnDestroy {
  @Output() closePanel = new EventEmitter<void>()

  totalCommands = 0
  commandsByAccountId: Record<string, Command[]> = {}
  allCommands: Command[] = []
  reconDate?: Date
  sessionId?: string
  isSubmitting = false
  _isVisible = false

  @Input()
  set isVisible(value: boolean) {
    this._isVisible = value
    if (!value) {
      return
    }

    this.setCommands()
  }

  readonly commands$ = this.store.select(selectCommands).pipe(
    map(({ commands }) => {
      this.allCommands = commands
      this.totalCommands = commands.length
      this.commandsByAccountId = this.groupCommandsByAccountId(commands)
      this.changeDetector.detectChanges()
    }),
  )

  private readonly destroySubject = new Subject<void>()

  constructor(
    private readonly store: Store<RootState>,
    private readonly commandService: CommandService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly hotkeyService: HotkeysService<
      'run-without-etl' | 'run-with-etl'
    >,
    private readonly toastService: ToastService,
    private readonly modalService: NgbModal,
    // eslint-disable-next-line @nrwl/nx/enforce-module-boundaries
    private readonly genericConfirmationModalService: GenericConfirmationModalService,
  ) {
    this.store
      .select(selectSelectedReconDate)
      .pipe(
        takeUntil(this.destroySubject),
        distinctUntilChanged(),
        tap((date: Date | undefined) => {
          if (date != null) {
            this.reconDate = date
          }
        }),
      )
      .subscribe()
  }

  ngOnDestroy(): void {
    this.destroySubject.next()
    this.destroySubject.complete()
  }

  ngOnInit() {
    this.commands$.pipe(takeUntil(this.destroySubject)).subscribe()

    this.store
      .select(selectActiveSession)
      .pipe(
        takeUntil(this.destroySubject),
        distinctUntilChanged((a, b) => a?.id === b?.id),
      )
      .subscribe(session => {
        this.sessionId = session?.id
      })

    this.hotkeyService.bind('run-with-etl', () => {
      this.runCommands(true, 'CUSTODY')
    })
    this.hotkeyService.bind('run-without-etl', () => {
      this.runCommands(false, 'CUSTODY')
    })
  }

  /** @internal */
  async setCommands() {
    if (!this.sessionId) {
      return
    }

    const commands = await this.commandService
      .getCommands(this.sessionId)
      .toPromise()
    this.store.dispatch(setCommands({ commands }))
  }

  async submitCommands(
    withEtls: boolean,
    runType: 'CUSTODY' | 'TRADING',
    accountId?: string,
  ) {
    if (!this.sessionId) {
      return
    }
    let accountsToRun: undefined | number[]

    if (accountId) {
      accountsToRun = [Number(accountId)]
    }

    this.isSubmitting = true
    const jobIds = await this.commandService
      .runSession(this.sessionId, withEtls, runType, 1, accountsToRun)
      .pipe(take(1))
      .toPromise()

    this.isSubmitting = false
    this.changeDetector.detectChanges()
    if (jobIds == null) {
      this.toastService.error('Commands failed to be submitted')
    } else {
      this.toastService.success('Commands submitted successfully')
    }
  }

  async runCommands(
    withEtls: boolean,
    runType: 'CUSTODY' | 'TRADING',
    accountId?: string,
  ) {
    if (accountId) {
      const commandsForAccount = this.commandsByAccountId[accountId] ?? []
      const reviewCommands = commandsForAccount.filter(c => c.needsReview)

      if (reviewCommands.length > 0) {
        if (
          this.commandService.checkForDuplicateCreateRequest(Number(accountId))
        ) {
          return
        }
        const message =
          this.commandService.buildRestrictedActionConfirmationMessage(
            reviewCommands.length,
          )
        const confirmed = await this.genericConfirmationModalService.warning(
          message,
          'Approval Required',
        )
        if (confirmed) {
          this.submitApprovalCommands(
            reviewCommands,
            commandsForAccount.length,
            withEtls,
            runType,
          )
        }
      } else {
        this.submitCommands(withEtls, runType, accountId)
      }
    } else {
      if (this.allCommands.some(c => c.needsReview)) {
        // Collect per-account review/non-review split
        const reviewCommandsByAccount: {
          accId: string
          reviewCommands: Command[]
          total: number
        }[] = []
        for (const accId of Object.keys(this.commandsByAccountId)) {
          const commandsPerAccount = this.commandsByAccountId[accId]
          const accountReviewCommands = commandsPerAccount.filter(
            c => c.needsReview,
          )
          if (accountReviewCommands.length > 0) {
            reviewCommandsByAccount.push({
              accId,
              reviewCommands: accountReviewCommands,
              total: commandsPerAccount.length,
            })
          } else {
            this.submitCommands(withEtls, runType, accId)
          }
        }

        // Show one confirmation dialog for all restricted commands across accounts
        if (reviewCommandsByAccount.length > 0) {
          const allReviewCommands = reviewCommandsByAccount.reduce<Command[]>(
            (acc, e) => acc.concat(e.reviewCommands),
            [],
          )
          const message =
            this.commandService.buildRestrictedActionConfirmationMessage(
              allReviewCommands.length,
            )
          const confirmed = await this.genericConfirmationModalService.warning(
            message,
            'Approval Required',
          )

          if (confirmed) {
            this.isSubmitting = true
            const results = await Promise.all(
              reviewCommandsByAccount.map(
                async ({ accId, reviewCommands, total }) => {
                  if (
                    this.commandService.checkForDuplicateCreateRequest(
                      Number(accId),
                    )
                  ) {
                    return null
                  }
                  try {
                    await this.submitApprovalCommands(
                      reviewCommands,
                      total,
                      withEtls,
                      runType,
                    )
                    return true
                  } catch {
                    return false
                  }
                },
              ),
            )
            this.isSubmitting = false
            this.changeDetector.detectChanges()
            const failedCount = results.filter(r => r === false).length
            const successCount = results.filter(r => r === true).length
            if (failedCount > 0) {
              this.toastService.error(
                `${failedCount} account(s) failed to submit approval commands`,
              )
            } else if (successCount > 0) {
              this.toastService.success(
                'Commands are pending on approvals panel',
              )
            }
          }
        }
      } else {
        this.submitCommands(withEtls, runType)
      }
    }
  }

  async submitApprovalCommands(
    reviewCommands: Command[],
    totalCommands: number,
    runEtls: boolean,
    runType: 'CUSTODY' | 'TRADING',
  ) {
    if (!this.sessionId) {
      return
    }
    const accountIds = [
      ...new Set(reviewCommands.map(c => c.command.accountId)),
    ]

    const reconDateString = toISOFormat(this.reconDate) + ''

    const request = this.commandService.buildRestrictedActionRequest(
      reviewCommands,
      accountIds,
      reconDateString,
      this.sessionId,
      runEtls,
      runType,
      totalCommands,
    )

    try {
      const response = await this.commandService
        .runRestrictedAction(request)
        .pipe(take(1))
        .toPromise()

      if (!response || (response?.invalidRequests?.length ?? 0) > 0) {
        this.toastService.error('Commands failed to be submitted')
      } else {
        this.toastService.success('Commands are pending on approvals panel')
      }
    } finally {
      this.isSubmitting = false
    }
  }

  /** @internal */
  groupCommandsByAccountId(commands: Command[]): Record<string, Command[]> {
    const baseObj: Record<string, Command[]> = {}
    return commands.reduce((acc, command) => {
      const { accountId } = command.command
      if (!acc[accountId]) {
        acc[accountId] = []
      }
      acc[accountId].push(command)
      return acc
    }, baseObj)
  }
}
